import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCompanySettings, getDefrevData, getEmployees, getGLLineMappings, saveCommissionAccrual } from '../api';
import { accruedCommissionsLeafKeysFromMeta, commissionAccountRefusal, commissionAccrualFromSettings, validateCommissionAccrual,
  COMMISSION_ACCRUAL_BASES, COMMISSION_ACCRUAL_COLUMNS, COMMISSION_BOOKS } from '../engines/commissionAccrual';
import { overlayLeafLabels } from '../utils/leafLabels';
import { COMMISSION_ACCRUAL_BASIS_EXPLANATIONS, COMMISSION_ACCRUAL_BASIS_LABELS, COMMISSION_BOOKS_EXPLANATIONS, COMMISSION_BOOKS_LABELS,
  commissionAccrualSummary, commissionAnswerMissing, commissionedRepCount, emptyAccountListMessage, hasNonMonthlyCommissionReps } from '../utils/commissionAccrualDisplay';

/* CommissionAccrualPanel -- THE STEP-5 "COMMISSIONS" PANEL (commission ship 5, 2026-10-10; the PaySchedulePanel pattern).
 *
 * The ONLY writer of how the books treat commissions (Brian Q3 a: wizard-only; the route is super-admin-only; Q10 a later
 * change goes through Brian). It sits on Step 5's "Employees already imported" screen: the answer depends on the STORED
 * roster -- the basis question is asked only when a sales rep is on a non-monthly cycle, and the route counts the reps from
 * the employees table -- so it is asked after the Step-5 import, where the panel and the route read the same roster.
 *
 * WHAT IT MUST NOT DO (Brian C1; CLAUDE.md 12.5 rule 92): pre-select an answer, suggest an account by its name, or guess any
 * answer. A company that was never asked opens with every field blank. A company with no commissioned rep is not asked
 * (the panel shows nothing unless an answer is already stored).
 *
 * WHERE EACH RULE LIVES (this file owns none of them):
 *   what is required when, every refusal's words      engines/commissionAccrual.js validateCommissionAccrual +
 *                                                      commissionAccountRefusal (wall mirror M18 of the server's) -- shown
 *                                                      here BEFORE the request; the server stays the judge
 *   which accounts may carry the accrual (Q5 a)       engines/commissionAccrual.js accruedCommissionsLeafKeysFromMeta -- the
 *                                                      STORED Step-4 role 'accrued' minus the accrued-payroll account
 *   when the answer is required / the basis is asked  utils/commissionAccrualDisplay.js (commissionAnswerMissing,
 *                                                      hasNonMonthlyCommissionReps -- the route's own count)
 *   an account's label                                 its owner, gl_line_mappings.display_name (utils/leafLabels.js)
 *
 * `onStored({ stored, missing } | null)` tells the wizard what is STORED (after the load and after every save) and the gate's
 * sentence (null = nothing missing). null = the stored answer could not be read (the wizard keeps its continue closed).
 * Plain ASCII on purpose (rule 55). */

const FONT = "'DM Sans',-apple-system,sans-serif";
const label = { fontSize: 13, fontFamily: FONT, color: '#1A1C2E', fontWeight: 500, display: 'block', marginBottom: 6 };
const field = { padding: '8px 12px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 14, fontFamily: FONT, minWidth: 220, background: '#fff', color: '#1A1C2E' };
const row = { marginBottom: 14 };
const note = { fontSize: 12, color: '#555', fontFamily: FONT, lineHeight: 1.5 };
const choice = { display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, fontSize: 13, fontFamily: FONT, color: '#1A1C2E', cursor: 'pointer' };

const BLANK = { books: '', account: '', basis: '' };
const draftOf = stored => ({
  books: (stored && stored.commission_books) || '',
  account: (stored && stored.accrued_commissions_bs_line_key) || '',
  basis: (stored && stored.commission_accrual_basis) || '',
});
const storedOf = data => Object.fromEntries(COMMISSION_ACCRUAL_COLUMNS.map(k => [k, data && data[k] !== undefined ? data[k] : null]));

/* THE REQUEST BODY -- the WHOLE answer; a field that does not apply is OMITTED (so "Expensed when paid" sends
   { commission_books: 'when_paid' } and nothing else). The basis is sent only when it is asked. A field that applies and is
   blank is sent as null, which the rule answers with its "required" message. */
export function commissionAccrualBody(draft, basisAsked) {
  const body = { commission_books: draft.books || null };
  if (draft.books !== 'accrues') return body;
  body.accrued_commissions_bs_line_key = draft.account || null;
  if (basisAsked) body.commission_accrual_basis = draft.basis || null;
  return body;
}

export default function CommissionAccrualPanel({ companyId, onStored }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [stored, setStored] = useState(null);      // the three stored values (settings names), once read
  const [payrollKey, setPayrollKey] = useState(null);   // companies.accrued_payroll_bs_line_key (never offered)
  const [employees, setEmployees] = useState([]);
  const [meta, setMeta] = useState({});            // bs_actuals_meta, as stored (the picker's list is judged on THIS)
  const [labels, setLabels] = useState({});        // the same, with each label from its owner
  const [draft, setDraft] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);      // the last save's { relinked }
  const onStoredRef = useRef(onStored);
  onStoredRef.current = onStored;
  const tell = useCallback(st => { if (onStoredRef.current) onStoredRef.current(st); }, []);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setLoadError(''); setError(''); setResult(null);
    try {
      const [sRes, dRes, eRes, gRes] = await Promise.all([
        getCompanySettings(companyId),
        getDefrevData(companyId),
        getEmployees(companyId),
        getGLLineMappings(companyId).catch(() => ({ data: [] })),   // no owner row -> the cached label (leafLabels: the mid-wizard fallback)
      ]);
      const s = sRes.data || {};
      const st = storedOf(s);
      const emps = eRes.data || [];
      const m = (dRes.data && dRes.data.bs_actuals_meta) || {};
      setStored(st); setPayrollKey(s.accrued_payroll_bs_line_key || null); setEmployees(emps);
      setMeta(m); setLabels(overlayLeafLabels(m, gRes.data || [])); setDraft(draftOf(st));
      tell({ stored: st, missing: commissionAnswerMissing(emps, st) });
    } catch (e) {
      /* A FAILED READ IS NOT "NEVER ANSWERED" and not "not required": the wizard's continue stays closed, and the reason is said. */
      setStored(null); setLoadError((e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Unknown error');
      tell(null);
    } finally { setLoading(false); }
  }, [companyId, tell]);
  useEffect(() => { load(); }, [load]);

  const accountKeys = useMemo(() => accruedCommissionsLeafKeysFromMeta(meta, payrollKey), [meta, payrollKey]);
  const labelOf = useCallback(k => (labels[k] && labels[k].label) || (meta[k] && meta[k].label) || k, [labels, meta]);
  const basisAsked = useMemo(() => hasNonMonthlyCommissionReps(employees), [employees]);
  const repCount = useMemo(() => commissionedRepCount(employees), [employees]);

  const set = patch => { setDraft(d => ({ ...d, ...patch })); setError(''); };
  const accrues = draft.books === 'accrues';
  const dirty = stored ? JSON.stringify(draftOf(stored)) !== JSON.stringify(draft) : false;

  const save = async () => {
    if (saving) return;
    setError(''); setResult(null);
    const body = commissionAccrualBody(draft, basisAsked);
    const v = validateCommissionAccrual(body, { hasNonMonthlyReps: basisAsked });
    if (v.refusal) { setError(v.refusal.error); return; }                        // the server's own words, before any request
    if (body.accrued_commissions_bs_line_key) {
      const r = commissionAccountRefusal(body.accrued_commissions_bs_line_key, meta, payrollKey);
      if (r) { setError(r.error); return; }
    }
    setSaving(true);
    try {
      const res = await saveCommissionAccrual(companyId, body);
      const st = storedOf(res.data && res.data.commission_accrual);
      setStored(st); setDraft(draftOf(st));
      setResult({ relinked: (res.data && res.data.relinked) || {} });
      tell({ stored: st, missing: commissionAnswerMissing(employees, st) });
    } catch (e) {
      // a refusal: its message, and nothing changes -- the stored answer and the typed draft both stay as they were
      setError((e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Unknown error');
    } finally { setSaving(false); }
  };

  const box = { border: '1px solid #E0E0E0', borderRadius: 8, padding: '18px 20px', marginBottom: 24, background: '#fff' };
  if (loading) return <div data-testid="commission-accrual-panel" style={{ ...box, color: '#999', fontFamily: FONT, fontSize: 13 }}>Loading the commission answer...</div>;
  if (loadError) return (
    <div data-testid="commission-accrual-panel" style={box}>
      <div data-testid="commission-accrual-load-error" style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 10 }}>Could not read the commission answer: {loadError}</div>
      <button type="button" onClick={load} style={{ ...field, minWidth: 0, cursor: 'pointer' }}>Retry</button>
    </div>
  );
  // A company with no commissioned rep is not asked (C1); one that already stored an answer still sees it.
  if (!repCount && !(stored && stored.commission_books)) return null;

  const storedAccrual = commissionAccrualFromSettings(stored);
  const storedAccount = stored && stored.accrued_commissions_bs_line_key;
  const rel = (result && result.relinked) || {};
  return (
    <div data-testid="commission-accrual-panel" style={box}>
      <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 6px' }}>Commissions</h4>
      <div style={{ ...note, marginBottom: 16 }}>How this company's books treat sales commissions. {repCount} rep{repCount === 1 ? ' earns' : 's earn'} commission.</div>

      <div style={row} role="radiogroup" aria-labelledby="commission-books-label">
        <span id="commission-books-label" style={label}>Commissions are:</span>
        {COMMISSION_BOOKS.map(b => (
          <label key={b} style={choice}>
            <input type="radio" name="commission-books" value={b} checked={draft.books === b} onChange={() => set({ books: b })} />
            <span><span style={{ fontWeight: 600 }}>{COMMISSION_BOOKS_LABELS[b]}</span><span data-testid={'commission-books-explain-' + b} style={{ ...note, display: 'block' }}>{COMMISSION_BOOKS_EXPLANATIONS[b]}</span></span>
          </label>
        ))}
      </div>

      {accrues && <>
        <div style={row}>
          <label style={label} htmlFor="accrued-commissions-account">Accrued-commissions account</label>
          {accountKeys.length === 0
            ? <div data-testid="commission-accrual-no-accounts" style={{ color: '#B5543A', fontSize: 13, fontFamily: FONT }}>{emptyAccountListMessage(meta, payrollKey)}</div>
            : <select id="accrued-commissions-account" value={draft.account} onChange={e => set({ account: e.target.value })} style={field}>
                <option value="">Choose...</option>
                {accountKeys.map(k => <option key={k} value={k}>{labelOf(k)}</option>)}
              </select>}
        </div>
        {basisAsked && (
          <div style={row} role="radiogroup" aria-labelledby="commission-basis-label" data-testid="commission-basis">
            <span id="commission-basis-label" style={label}>The books accrue commissions:</span>
            {COMMISSION_ACCRUAL_BASES.map(b => (
              <label key={b} style={choice}>
                <input type="radio" name="commission-basis" value={b} checked={draft.basis === b} onChange={() => set({ basis: b })} />
                <span><span style={{ fontWeight: 600 }}>{COMMISSION_ACCRUAL_BASIS_LABELS[b]}</span><span data-testid={'commission-basis-explain-' + b} style={{ ...note, display: 'block' }}>{COMMISSION_ACCRUAL_BASIS_EXPLANATIONS[b]}</span></span>
              </label>
            ))}
          </div>
        )}
      </>}

      {error && <div data-testid="commission-accrual-error" style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{error}</div>}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button type="button" onClick={save} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#1A73E8', color: '#fff', fontSize: 13, fontWeight: 600, fontFamily: FONT, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.5 : 1 }}>
          {saving ? 'Saving...' : 'Save commission answer'}
        </button>
        {dirty && !saving && <span data-testid="commission-accrual-unsaved" style={{ fontSize: 12, color: '#B5731A', fontFamily: FONT }}>Unsaved changes {'—'} Save to apply.</span>}
      </div>

      <div data-testid="commission-accrual-stored" style={{ ...note, marginTop: 14, paddingTop: 12, borderTop: '1px solid #F0F0F0' }}>
        <span style={{ fontWeight: 600 }}>Stored: </span>
        {stored && stored.commission_books ? commissionAccrualSummary(storedAccrual, storedAccount ? labelOf(storedAccount) : null) : 'not answered yet'}
      </div>
      {result && (rel.linked || rel.unlinked) && (
        <div data-testid="commission-accrual-relinked" style={{ ...note, marginTop: 8 }}>
          {rel.linked && <div>Linked: {labelOf(rel.linked)} now carries the accrued commissions in forecast months.</div>}
          {rel.created && rel.linked && <div>Created: {labelOf(rel.linked)} had no forecast assumption, so one was created for it.</div>}
          {rel.unlinked && <div>Unlinked: {labelOf(rel.unlinked)} is held at its own balance again (a constant).</div>}
        </div>
      )}
    </div>
  );
}
